/**
  Author: Lokesh Arora - lokesh.arora@enabledata.com
  Purpose: Change password page
  Date: April 04, 2020
**/

import React from 'react';
import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Typography,
  Tooltip,
  InputAdornment,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useStoreState, useStoreActions } from 'easy-peasy';
import { stringConstants } from '../../constants/stringConstants';
import LocalStorageService from '../../services/localStorage';
// import Avatar from '@material-ui/core/Avatar';

import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
//import commonPasswords from '../../constants/commonPasswords'

const localStorageService = LocalStorageService.getService();

/** STYLES HERE * */
const useStyles = makeStyles((theme) => ({
  paper: {
    margin: '5px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: 0,
  },
  submit: {
    margin: '16px 0px 10px',
  },
  large: {
    width: theme.spacing(9),
    height: theme.spacing(9),
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
    fontSize: '1.9rem',
    alignSelf: 'center'
  },
  primary: {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    backgroundColor: theme.palette.primary.main,
  },
  userEmail: {
    color: theme.palette.primary.main,
    fontWeight: '700',
    marginTop: '16px'
  }
}));

const ChangePasswordPage = () => {
  const classes = useStyles();
  const userAction = useStoreActions((actions) => actions.login);
  const userState = useStoreState((state) => state.login);
  const commonAction = useStoreActions((actions) => actions.common);

  const [validPassword, setValidPassword] = React.useState(false);
  const [passwordErrorMsg, setPasswordErrorMsg] = React.useState(null);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = React.useState(false);
  const [submitLoader, setSubmitLoader] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = () => setShowPassword(!showPassword);

  const handleClickShowNewPassword = () => setShowNewPassword(!showNewPassword);
  const handleMouseDownNewPassword = () => setShowNewPassword(!showNewPassword);

  const handleClickShowConfirmNewPassword = () => setShowConfirmNewPassword(!showConfirmNewPassword);
  const handleMouseDownConfirmNewPassword = () => setShowConfirmNewPassword(!showConfirmNewPassword);



  // let avatarLabel = '-';

  let userInfo = localStorageService.getUserInfo();
  if (userInfo != null && userInfo != undefined) {
    userInfo = JSON.parse(userInfo);
    // let nameInitials = '-';
    // if (userInfo?.firstname?.length > 0) {
    //   nameInitials = userInfo.firstname[0].toUpperCase();
    // }
    // if (userInfo?.lastname?.length > 0) {
    //   nameInitials = nameInitials + userInfo.lastname[0].toUpperCase();
    // }
    // avatarLabel = nameInitials;
    if (userInfo.force_change_password) {
      userAction.setForceChangePassword(true);
    }
  }

  // Set initial values top empty for email and password
  const [data] = React.useState({
    current_password: '',
    passwd: '',
    passwd_confirmation: '',
  });

  /**
   * On Change Password submit, trigger a API call
   * @param {object} values password and confirm password
   */
  const onSave = async (values, { setErrors }) => {
    setSubmitLoader(true);
    values.username = userState.username;
    userAction
      .changePassword(values)
      .then((response) => {
        setSubmitLoader(false);
        if (response && response.data && response.data.success) {
          commonAction.setSubmitSnackbar({
            status: true,
            messages: stringConstants.passwordChangedMsg,
          });
          userAction.setForceChangePassword(false);
          setTimeout(() => {
            userAction.logout();
            window.location = '/';
          }, 2000);
        }
      })
      .catch((error) => {
        setSubmitLoader(false);
        if (error.response && error.response.data && error.response.data.error) {
          const outputErrors = {};
          const errorlist = error.response.data.error;
          for (const item in errorlist) {
            outputErrors[Object.keys(errorlist[item])] =
              errorlist[item][Object.keys(errorlist[item])];
          }
          setErrors(outputErrors);
        }
      });
  };

  function checkSequentialString(s) {
      // Check for sequential numerical characters
      for(let i in s) 
          if (+s[+i+1] == +s[i]+1 && 
              +s[+i+2] == +s[i]+2) return false;
      // Check for sequential alphabetical characters
      for(let i in s) 
          if (String.fromCharCode(s.charCodeAt(i)+1) == s[+i+1] && 
              String.fromCharCode(s.charCodeAt(i)+2) == s[+i+2]) return false;
      return true;
  }

  
  /**
   *  Yup schema for validation of form fields
   */
  const schema = Yup.object().shape({
    current_password: Yup.string().required(stringConstants.requiredValidationMsg),
    passwd: Yup.string()
      .max(25, 'Password must not be longer than 25 characters')
      .required(stringConstants.requiredValidationMsg)
      .test('passwordStrengthCheck', 'The password does not meet minimum criteria', 
        function(value) {
          const pwdRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{12,})/;
          let errorMsg = '';
          if (!(/^(?=.*[a-z])(?=.*[A-Z])/.test(value))) {
            errorMsg += "Must have atleast one upper and lower case character \n"
          }
          if (!(/^(?=.*[0-9])/.test(value))) {
            errorMsg += "Must have atleast one numeric character \n"
          }
          if (!(/^(?=.*[!@#\$%\^&\*])/.test(value))) {
            errorMsg += "Must have atleast one special character \n"
          }
          if (!checkSequentialString(value)) {
            errorMsg += "Must not have alpha or numeric sequences \n"
          }
          if (!(/^(?=.{12,})/.test(value))) {
            errorMsg += "Must have 12 characters \n"
          }
          setPasswordErrorMsg(errorMsg);
          let isValidPassword = pwdRegex.test(value) && checkSequentialString(value);
          setValidPassword(isValidPassword);
          return isValidPassword;
        }),
      /*.matches(
        /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[*.!@$%^&\(\)\{\}\[\]\:\;\<\>\,\.\?\/\~\_\+\-\=\|\\])/g,// NOSONAR
        'Password should have atleast 1 digit, 1 lower case character,1 upper case character and a special character',
      ),*/
    passwd_confirmation: Yup.string()
      .required(stringConstants.requiredValidationMsg)
      .oneOf([Yup.ref('passwd'), null], stringConstants.passwordMatchMsg),
  });

  return (
    <Grid container>
      <CssBaseline />
      <Grid item md={12}>
        <div className={classes.paper}>
          {/* <div style={{
            display: 'flex',
            flexDirection: 'column',

          }}>
            <Avatar className={classes.large} color="primary">{avatarLabel}</Avatar>
            <div className={classes.userEmail}>{userInfo?.email}</div>
          </div> */}
          {userState.forceChangePassword && (
            <Typography color="error">
              You have logged in with your temporary Password and this is for one
              time use, Please change your password
            </Typography>
          )}
          <Formik
            initialValues={data}
            onSubmit={onSave}
            validationSchema={schema}
          >
            {({
              values,
              touched,
              errors,
              handleChange,
              handleBlur,
              handleSubmit,
            }) => (
              <form onSubmit={handleSubmit} className={classes.form} noValidate>
                <TextField
                  variant="outlined"
                  margin="normal"
                  value={values.current_password}
                  required
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.current_password && touched.current_password}
                  helperText={
                    errors.current_password && touched.current_password && errors.current_password
                  }
                  name="current_password"
                  label="Current Password"
                  type={showPassword ? "text" : "password"} 
                  id="current_password"
                  InputProps={{ // <-- This is where the toggle button is added.
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />

                <TextField
                  variant="outlined"
                  margin="normal"
                  value={values.passwd}
                  required
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.passwd && touched.passwd}
                  helperText={
                    errors.passwd && touched.passwd && errors.passwd
                  }
                  InputProps={{
                    endAdornment:
                    (
                      <>
                        {touched.passwd && validPassword && (<CheckCircleIcon style={{color:'green'}} />)}
                        {touched.passwd && !validPassword && (<Tooltip title={<p style={{margin: '0px 0px 0px 0px', padding: '10px 10px 10px 10px'}}><span style={{ whiteSpace: 'pre-line', cursor: 'pointer' }}>{passwordErrorMsg}</span></p>}><CancelIcon style={{color:'red', cursor: 'pointer'}} /></Tooltip>)}

                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowNewPassword}
                          onMouseDown={handleMouseDownNewPassword}
                        >
                          {showNewPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </>
                      
                    )
                  }}
                  name="passwd"
                  label="Password"
                  type={showNewPassword ? "text" : "password"}
                  id="passwd"
                />
                <TextField
                  variant="outlined"
                  margin="normal"
                  value={values.passwd_confirmation}
                  required
                  fullWidth
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    errors.passwd_confirmation &&
                    touched.passwd_confirmation
                  }
                  helperText={
                    errors.passwd_confirmation &&
                    touched.passwd_confirmation &&
                    errors.passwd_confirmation
                  }
                  name="passwd_confirmation"
                  label="Password Confirmation"
                  type={showConfirmNewPassword ? "text" : "password"}
                  id="passwd_confirmation"
                  InputProps={{ // <-- This is where the toggle button is added.
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowConfirmNewPassword}
                          onMouseDown={handleMouseDownConfirmNewPassword}
                        >
                          {showConfirmNewPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.submit}
                  disabled={submitLoader}
                >
                  {submitLoader && (
                    <CircularProgress size={20} color="white" style={{marginRight: '5px'}} />
                  )}
                  Change Password
                </Button>
              </form>
            )}
          </Formik>
        </div>
      </Grid>
    </Grid>
  );
};

export default withRouter(ChangePasswordPage);
